import { c as _c } from "react/compiler-runtime";
import { IcoChevronRight16 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import { type FocusEventHandler, type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
type PlanBlockProps = {
  id: string;
  isEditing?: boolean;
  isDisabled?: boolean;
  headerTitle: ReactNode;
  headerActions?: ReactNode;
  isHighlighted?: boolean;
  footerIcon?: ReactNode;
  footerTitle: string;
  footerRef: string;
  contentClassName?: string;
  children: ReactNode;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  /** Adds a header chevron; when collapsed, the body is clipped behind a fade + expand pill. */
  collapsible?: boolean;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  collapseAriaLabel?: string;
  /** Adds 4px between the chevron and the title (loosens the default tight gap). */
  titleExtraGap?: boolean;
};

// Collapsed cards keep the full card shell but clip the real content to this height —
// enough to always show the prompt plus the first outputs / rules branches.
const COLLAPSED_CONTENT_HEIGHT = 260;
export const PlanBlock = t0 => {
  const $ = _c(67);
  const {
    id,
    isEditing,
    isDisabled,
    headerTitle,
    headerActions,
    isHighlighted,
    footerIcon,
    footerTitle,
    footerRef,
    contentClassName: t1,
    children,
    onBlur,
    collapsible,
    collapsed,
    onToggleCollapsed,
    collapseAriaLabel,
    titleExtraGap
  } = t0;
  const contentClassName = t1 === undefined ? "gap-5" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "agentBuilder.canvas.common"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t2);
  const isCollapsed = collapsible && collapsed;
  const clampRef = useRef(null);
  const [isClipped, setIsClipped] = useState(true);
  let t3;
  let t4;
  if ($[1] !== isCollapsed) {
    t3 = () => {
      const el = clampRef.current;
      if (!isCollapsed || !el) {
        return;
      }
      const measure = () => setIsClipped(el.scrollHeight > el.clientHeight + 2);
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      return () => observer.disconnect();
    };
    t4 = [isCollapsed];
    $[1] = isCollapsed;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  const t5 = isEditing ? "border-transparent" : "border-transparent hover:border-tertiary";
  const t6 = isDisabled && "opacity-50 pointer-events-none";
  let t7;
  if ($[4] !== t5 || $[5] !== t6) {
    t7 = cx("group rounded-[10px] px-0.5 transition-colors bg-senary/50 border", t5, t6);
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = collapsible ? "px-1" : "px-2";
  const t9 = isEditing && "sticky top-0 z-10 bg-senary rounded-[10px]";
  let t10;
  if ($[7] !== t8 || $[8] !== t9) {
    t10 = cx("h-8 flex items-center gap-1", t8, t9);
    $[7] = t8;
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== collapseAriaLabel || $[11] !== collapsed || $[12] !== collapsible || $[13] !== onToggleCollapsed) {
    t11 = collapsible && <button type="button" aria-label={collapseAriaLabel} aria-expanded={!collapsed} onClick={onToggleCollapsed} className="-mr-1 shrink-0 size-6 flex items-center justify-center rounded-(--icon-button-border-radius-tiny) outline-none transition-colors hover:bg-hover-control focus-visible:ring-1 focus-visible:ring-accent cursor-pointer"><IcoChevronRight16 className={cx("transition-transform", !collapsed && "rotate-90")} color="tertiary" /></button>;
    $[10] = collapseAriaLabel;
    $[11] = collapsed;
    $[12] = collapsible;
    $[13] = onToggleCollapsed;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const t12 = titleExtraGap && "ml-1";
  let t13;
  if ($[15] !== t12) {
    t13 = cx("flex h-7 min-w-0 flex-1 items-center text-label-3 text-primary", t12);
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== headerTitle || $[18] !== t13) {
    t14 = <div className={t13}>{headerTitle}</div>;
    $[17] = headerTitle;
    $[18] = t13;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== headerActions) {
    t15 = headerActions && <div className="flex items-center gap-2 shrink-0">{headerActions}</div>;
    $[20] = headerActions;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t14 || $[25] !== t15) {
    t16 = <div className={t10}>{t11}{t14}{t15}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t14;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const t17 = isCollapsed && "overflow-hidden";
  const t18 = isHighlighted ? "bg-active" : "bg-primary";
  let t19;
  if ($[27] !== t17 || $[28] !== t18) {
    t19 = cx("relative rounded-md border border-tertiary transition-colors duration-300", t17, t18);
    $[27] = t17;
    $[28] = t18;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  const t20 = isCollapsed ? onToggleCollapsed : undefined;
  const t21 = isCollapsed ? "overflow-hidden pb-3" : "pb-4";
  let t22;
  if ($[30] !== contentClassName || $[31] !== t21) {
    t22 = cx("flex flex-col px-4 pt-3", contentClassName, t21);
    $[30] = contentClassName;
    $[31] = t21;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  let t23;
  if ($[33] !== isCollapsed) {
    t23 = isCollapsed ? {
      maxHeight: COLLAPSED_CONTENT_HEIGHT
    } : undefined;
    $[33] = isCollapsed;
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  let t24;
  if ($[35] !== children || $[36] !== t20 || $[37] !== t22 || $[38] !== t23) {
    t24 = <div ref={clampRef} onFocusCapture={t20} className={t22} style={t23}>{children}</div>;
    $[35] = children;
    $[36] = t20;
    $[37] = t22;
    $[38] = t23;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  let t25;
  if ($[40] !== isClipped || $[41] !== isCollapsed || $[42] !== isHighlighted || $[43] !== onToggleCollapsed || $[44] !== t) {
    t25 = isCollapsed && isClipped && <div className={cx("pointer-events-none absolute inset-x-0 bottom-0 flex h-20 items-end justify-center bg-gradient-to-t to-transparent pb-2.5 backdrop-blur-[1px]", isHighlighted ? "from-active via-active/80" : "from-primary via-primary/80")}><button type="button" aria-hidden={true} tabIndex={-1} onClick={onToggleCollapsed} className="pointer-events-auto rounded-md border border-tertiary bg-primary px-2.5 py-1 text-label-3 font-medium text-secondary shadow-sm outline-none transition cursor-pointer md:opacity-0 md:group-hover:opacity-100 hover:bg-hover focus-visible:opacity-100 focus-visible:ring-1 focus-visible:ring-accent">{t(_temp)}</button></div>;
    $[40] = isClipped;
    $[41] = isCollapsed;
    $[42] = isHighlighted;
    $[43] = onToggleCollapsed;
    $[44] = t;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== t19 || $[47] !== t24 || $[48] !== t25) {
    t26 = <div className={t19}>{t24}{t25}</div>;
    $[46] = t19;
    $[47] = t24;
    $[48] = t25;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== footerTitle) {
    t27 = <span>{footerTitle}</span>;
    $[50] = footerTitle;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== footerIcon || $[53] !== t27) {
    t28 = <div className="flex items-center gap-1 text-caption-3 font-medium text-tertiary">{footerIcon}{t27}</div>;
    $[52] = footerIcon;
    $[53] = t27;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== footerRef) {
    t29 = <div className="text-caption-3 font-medium text-tertiary font-mono!">{footerRef}</div>;
    $[55] = footerRef;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] !== t28 || $[58] !== t29) {
    t30 = <div className="h-8 flex items-center justify-between px-2">{t28}{t29}</div>;
    $[57] = t28;
    $[58] = t29;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== id || $[61] !== onBlur || $[62] !== t16 || $[63] !== t26 || $[64] !== t30 || $[65] !== t7) {
    t31 = <div data-id={id} onBlur={onBlur} className={t7}>{t16}{t26}{t30}</div>;
    $[60] = id;
    $[61] = onBlur;
    $[62] = t16;
    $[63] = t26;
    $[64] = t30;
    $[65] = t7;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  return t31;
};
function _temp($) {
  return $.clickToExpand;
}